import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ArrowUp,
  ChevronRight,
  FolderPlus,
  HardDrive,
  Loader2,
  MoreHorizontal,
  PanelLeft,
  PanelLeftClose,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import {
  useAttachmentFolderMutations,
  useAttachmentFolders,
  type AttachmentFolderDTO,
} from "@/hooks/api/useAttachmentFolders";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FolderIcon3D } from "./FolderIcon3D";

// --- move options helper (used by Documents) ---

export function buildFolderMoveOptions(folders: AttachmentFolderDTO[]) {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const labelFor = (id: number): string => {
    const parts: string[] = [];
    let cur: number | undefined = id;
    const seen = new Set<number>();
    while (cur != null && !seen.has(cur)) {
      seen.add(cur);
      const f = byId.get(cur);
      if (!f) break;
      parts.unshift(f.name);
      cur = f.parentId ?? undefined;
    }
    return parts.join(" / ");
  };
  const opts = folders.map((f) => ({
    id: f.id as number,
    label: labelFor(f.id),
  }));
  opts.sort((a, b) => a.label.localeCompare(b.label));
  return [
    { id: null as number | null, label: "Root (no folder)" },
    ...opts,
  ];
}

function folderAncestors(
  folderId: number | null,
  all: AttachmentFolderDTO[]
): AttachmentFolderDTO[] {
  if (folderId == null) return [];
  const map = new Map(all.map((f) => [f.id, f]));
  const chain: AttachmentFolderDTO[] = [];
  let cur: number | null = folderId;
  const seen = new Set<number>();
  while (cur != null && !seen.has(cur)) {
    seen.add(cur);
    const f = map.get(cur);
    if (!f) break;
    chain.unshift(f);
    cur = f.parentId;
  }
  return chain;
}

type DocFilter = "all" | "work" | "payment";

type ExplorerProps = {
  attachedTo: string;
  attachedScope: string | number;
  currentFolderId: number | null;
  onNavigate: (folderId: number | null) => void;
  docFilter: DocFilter;
  onDocFilterChange: (v: DocFilter) => void;
  children: React.ReactNode;
  /** Rendered after the “New folder” control (e.g. download-all). */
  extraHeaderActions?: ReactNode;
  /**
   * Extra filter control rendered beside the All/Work/Payments strip — used
   * for the lead-or-deal grain picker, which only exists when the lead has
   * deals, so it can't be a fixed fourth tab.
   */
  extraFilterControls?: ReactNode;
};

function useTreeExpanded(
  foldersAttachment: AttachmentFolderDTO[],
  currentFolderId: number | null
) {
  const pathParentIds = useMemo(() => {
    const s = new Set<number>();
    if (currentFolderId == null) return s;
    const byId = new Map(foldersAttachment.map((f) => [f.id, f]));
    let cur: number | null = currentFolderId;
    const guard = new Set<number>();
    while (cur != null && !guard.has(cur)) {
      guard.add(cur);
      const f = byId.get(cur);
      if (!f) break;
      if (f.parentId != null) s.add(f.parentId);
      cur = f.parentId;
    }
    return s;
  }, [foldersAttachment, currentFolderId]);

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    setExpandedIds((prev) => {
      const n = new Set(prev);
      let changed = false;
      pathParentIds.forEach((id) => {
        if (!n.has(id)) {
          n.add(id);
          changed = true;
        }
      });
      // Must return `prev` when nothing changed — a new Set with the same
      // members is still a new reference and triggers an infinite re-render loop.
      return changed ? n : prev;
    });
  }, [pathParentIds]);

  const expanded = expandedIds;

  const toggle = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  return { expanded, toggle };
}

function FolderTreeRows({
  folders,
  parentId,
  depth,
  currentFolderId,
  onNavigate,
  expanded,
  toggleExpand,
  onDeleteFolder,
  onRenameRequest,
  deleteFolderPending,
  compact,
}: {
  folders: AttachmentFolderDTO[];
  parentId: number | null;
  depth: number;
  currentFolderId: number | null;
  onNavigate: (id: number | null) => void;
  expanded: Set<number>;
  toggleExpand: (id: number) => void;
  onDeleteFolder: (folder: AttachmentFolderDTO) => void;
  onRenameRequest: (folder: AttachmentFolderDTO) => void;
  deleteFolderPending: boolean;
  compact: boolean;
}) {
  const children = useMemo(
    () =>
      folders
        .filter((f) => (f.parentId ?? null) === parentId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [folders, parentId]
  );

  if (children.length === 0) return null;

  return (
    <>
      {children.map((folder) => {
        const subCount = folders.filter(
          (f) => (f.parentId ?? null) === folder.id
        ).length;
        const hasChildren = subCount > 0;
        const isOpen = expanded.has(folder.id);
        const isHere = currentFolderId === folder.id;

        const folderEmpty =
          (folder._count?.documents ?? 0) === 0 &&
          (folder._count?.children ?? 0) === 0;

        const folderOpenButton = (
          <button
            type="button"
            onClick={() => onNavigate(folder.id)}
            className={cn(
              "flex h-8 shrink-0 items-center rounded-md text-left text-[13px] leading-none transition-colors",
              compact
                ? "size-7 justify-center px-0"
                : "min-w-0 flex-1 gap-2 px-2",
              isHere
                ? "bg-[#d8e9ff] text-[#1a5fb4] font-medium shadow-[inset_0_0_0_1px_rgba(30,110,200,0.14)]"
                : "text-[#1e302a] hover:bg-black/[0.05]"
            )}
            aria-label={compact ? folder.name : undefined}
          >
            <span className="flex size-7 shrink-0 items-center justify-center">
              <FolderIcon3D size="sm" highlight={isHere} />
            </span>
            {!compact && (
              <span className="min-w-0 truncate">{folder.name}</span>
            )}
          </button>
        );

        return (
          <div key={folder.id} role="treeitem" aria-expanded={hasChildren ? isOpen : undefined}>
            <div
              className="group flex h-8 max-w-full flex-nowrap items-center gap-px"
              style={{ paddingLeft: compact ? depth * 3 : depth * 12 }}
            >
              <div
                className={cn(
                  "flex shrink-0 items-center justify-center",
                  compact ? "size-5" : "size-6"
                )}
              >
                {hasChildren ? (
                  <button
                    type="button"
                    className={cn(
                      "flex items-center justify-center rounded text-[#5c6560] hover:bg-black/[0.06]",
                      compact ? "size-5" : "size-6"
                    )}
                    aria-label={isOpen ? "Collapse" : "Expand"}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(folder.id);
                    }}
                  >
                    <ChevronRight
                      className={cn(
                        "shrink-0 transition-transform duration-150",
                        compact ? "size-3.5" : "size-[15px]",
                        isOpen && "rotate-90"
                      )}
                    />
                  </button>
                ) : (
                  <span
                    className={cn("shrink-0", compact ? "size-5" : "size-6")}
                    aria-hidden
                  />
                )}
              </div>
              {compact ? (
                <Tooltip>
                  <TooltipTrigger asChild>{folderOpenButton}</TooltipTrigger>
                  <TooltipContent side="right">{folder.name}</TooltipContent>
                </Tooltip>
              ) : (
                folderOpenButton
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "shrink-0 text-[#5c6560]",
                      compact ? "size-6 opacity-100" : "size-7 opacity-0 hover:opacity-100 group-hover:opacity-100"
                    )}
                    aria-label="Folder actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem
                    onClick={() => onRenameRequest(folder)}
                  >
                    <Pencil className="mr-2 size-3.5" />
                    Rename folder
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!folderEmpty || deleteFolderPending}
                    onClick={() => onDeleteFolder(folder)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="mr-2 size-3.5" />
                    Delete folder
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {hasChildren && isOpen && (
              <FolderTreeRows
                folders={folders}
                parentId={folder.id}
                depth={depth + 1}
                currentFolderId={currentFolderId}
                onNavigate={onNavigate}
                expanded={expanded}
                toggleExpand={toggleExpand}
                onDeleteFolder={onDeleteFolder}
                onRenameRequest={onRenameRequest}
                deleteFolderPending={deleteFolderPending}
                compact={compact}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

export default function AttachmentFileExplorer({
  attachedTo,
  attachedScope,
  currentFolderId,
  onNavigate,
  docFilter,
  onDocFilterChange,
  extraFilterControls,
  children,
  extraHeaderActions,
}: ExplorerProps) {
  const { data: folders = [], isLoading } = useAttachmentFolders(
    attachedTo,
    attachedScope
  );
  const { createFolder, deleteFolder, renameFolder } =
    useAttachmentFolderMutations(attachedTo, attachedScope);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameFolderId, setRenameFolderId] = useState<number | null>(null);
  const [renameName, setRenameName] = useState("");

  const [folderSidebarCollapsed, setFolderSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 1280px)").matches;
  });

  const { expanded, toggle } = useTreeExpanded(folders, currentFolderId);

  const crumbs = useMemo(
    () => folderAncestors(currentFolderId, folders),
    [currentFolderId, folders]
  );

  const parentIdOfCurrent = useMemo(() => {
    if (currentFolderId == null) return undefined;
    return folders.find((f) => f.id === currentFolderId)?.parentId ?? null;
  }, [currentFolderId, folders]);

  const handleDeleteFolder = useCallback(
    (folder: AttachmentFolderDTO) => {
      const empty =
        (folder._count?.documents ?? 0) === 0 &&
        (folder._count?.children ?? 0) === 0;
      if (!empty) return;
      deleteFolder.mutate(folder.id, {
        onSuccess: () => {
          if (currentFolderId === folder.id) {
            onNavigate(folder.parentId ?? null);
          }
        },
      });
    },
    [currentFolderId, deleteFolder, onNavigate]
  );

  const submitCreate = () => {
    const name = newName.trim();
    if (!name) return;
    createFolder.mutate(
      { name, parentId: currentFolderId },
      {
        onSuccess: () => {
          setNewName("");
          setCreateOpen(false);
        },
      }
    );
  };

  const openRenameDialog = useCallback((folder: AttachmentFolderDTO) => {
    setRenameFolderId(folder.id);
    setRenameName(folder.name);
    setRenameOpen(true);
  }, []);

  const submitRename = () => {
    const name = renameName.trim();
    if (!name || renameFolderId == null) return;
    renameFolder.mutate(
      { id: renameFolderId, name },
      {
        onSuccess: () => {
          setRenameOpen(false);
          setRenameFolderId(null);
          setRenameName("");
        },
      }
    );
  };

  const folderRailCompact = folderSidebarCollapsed;

  return (
    <div className="flex min-h-0 w-full min-h-[380px] flex-1 max-h-[min(85vh,720px)] items-stretch overflow-hidden rounded-xl bg-[#f4f6f8] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
      {/* —— Sidebar —— */}
      <aside
        className={cn(
          "flex shrink-0 flex-col self-stretch border-r border-black/[0.04] bg-[#f4f6f8] transition-[width] duration-200 ease-out",
          folderSidebarCollapsed ? "w-20" : "w-[200px]"
        )}
        aria-label="Folders"
      >
        <div
          className={cn(
            "flex border-b border-black/[0.04] bg-[#f4f6f8] py-2",
            folderSidebarCollapsed
              ? "flex-col items-center gap-1 px-1"
              : "items-start gap-2 px-3"
          )}
        >
          {!folderSidebarCollapsed && (
            <div className="min-w-0 flex-1">
              <p
                role="heading"
                aria-level={2}
                className="m-0 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-black-8)]"
              >
                Folders
              </p>
              <p className="text-b5 m-0 mt-1 leading-snug text-[var(--color-black-6)]">
                Click a folder to open it. Use arrows to expand.
              </p>
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-[var(--color-black-7)] hover:bg-black/[0.06]"
                aria-expanded={!folderSidebarCollapsed}
                aria-controls="folder-tree-panel"
                aria-label={
                  folderSidebarCollapsed
                    ? "Expand folders panel"
                    : "Collapse folders panel"
                }
                onClick={() => setFolderSidebarCollapsed((c) => !c)}
              >
                {folderSidebarCollapsed ? (
                  <PanelLeft className="size-4" />
                ) : (
                  <PanelLeftClose className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {folderSidebarCollapsed ? "Expand folders" : "Collapse folders"}
            </TooltipContent>
          </Tooltip>
        </div>
        <div
          id="folder-tree-panel"
          className={cn(
            "flex-1 overflow-x-hidden overflow-y-auto py-2",
            folderSidebarCollapsed ? "px-1" : "px-2"
          )}
          role="tree"
          aria-label="Folder tree"
        >
          {folderRailCompact ? (
            <div className="mb-1 flex h-8 items-center justify-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onNavigate(null)}
                    className={cn(
                      "flex size-8 items-center justify-center rounded-md transition-colors",
                      currentFolderId === null
                        ? "bg-[#d8e9ff] text-[#1a5fb4] shadow-[inset_0_0_0_1px_rgba(30,110,200,0.14)]"
                        : "text-[#1e302a] hover:bg-black/[0.05]"
                    )}
                    aria-label="All files"
                  >
                    <HardDrive
                      className="size-3.5 text-[#3584e4]"
                      strokeWidth={2}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">All files</TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <div className="mb-0.5 flex h-8 items-center">
              <div className="size-6 shrink-0" aria-hidden />
              <button
                type="button"
                onClick={() => onNavigate(null)}
                className={cn(
                  "flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-left text-[13px] font-medium leading-none transition-colors",
                  currentFolderId === null
                    ? "bg-[#d8e9ff] text-[#1a5fb4] shadow-[inset_0_0_0_1px_rgba(30,110,200,0.14)]"
                    : "text-[#1e302a] hover:bg-black/[0.05]"
                )}
              >
                <span className="flex size-7 shrink-0 items-center justify-center">
                  <HardDrive
                    className="size-3.5 text-[#3584e4]"
                    strokeWidth={2}
                  />
                </span>
                <span className="min-w-0 truncate">All files</span>
              </button>
            </div>
          )}
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="size-5 animate-spin text-[#3584e4]" />
            </div>
          ) : (
            <FolderTreeRows
              folders={folders}
              parentId={null}
              depth={0}
              currentFolderId={currentFolderId}
              onNavigate={onNavigate}
              expanded={expanded}
              toggleExpand={toggle}
              onDeleteFolder={handleDeleteFolder}
              onRenameRequest={openRenameDialog}
              deleteFolderPending={deleteFolder.isPending}
              compact={folderRailCompact}
            />
          )}
        </div>
      </aside>

      {/* —— Main explorer pane —— */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col self-stretch bg-[#f4f6f8]">
        <header className="box-border flex min-h-0 shrink-0 flex-col border-b border-black/[0.04] bg-[#f4f6f8] xl:h-[4.75rem] xl:min-h-[4.75rem] xl:max-h-[4.75rem]">
          {/* Row 1: on wide screens fixed height; below xl allow wrap for narrow lead drawer widths */}
          <div
            className={cn(
              "box-border flex min-w-0 items-center gap-2 px-3",
              "max-xl:min-h-10 max-xl:flex-wrap max-xl:py-2",
              "xl:h-10 xl:min-h-10 xl:max-h-10 xl:shrink-0 xl:flex-nowrap xl:py-0"
            )}
          >
            <div className="flex min-w-0 flex-wrap items-center gap-2 xl:flex-nowrap">
              <div className="flex shrink-0 items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0 text-[var(--color-black-7)] hover:bg-black/[0.06]"
                      disabled={currentFolderId === null}
                      onClick={() => onNavigate(parentIdOfCurrent ?? null)}
                      aria-label="Up one level"
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Up one folder</TooltipContent>
                </Tooltip>
              </div>

              <div
                className="inline-flex h-8 shrink-0 items-center rounded-lg bg-black/[0.05] p-0.5"
                role="tablist"
                aria-label="Filter documents"
              >
                {(
                  [
                    { k: "all" as const, l: "All" },
                    { k: "work" as const, l: "Work" },
                    { k: "payment" as const, l: "Payments" },
                  ] as const
                ).map(({ k, l }) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={docFilter === k}
                    onClick={() => onDocFilterChange(k)}
                    className={cn(
                      "h-7 shrink-0 whitespace-nowrap rounded-md px-2.5 text-[12px] font-medium leading-none transition-colors",
                      docFilter === k
                        ? "bg-white text-[#1e302a] shadow-sm"
                        : "text-[#5c6560] hover:text-[#1e302a]"
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>

              {extraFilterControls}
            </div>

            <div
              className={cn(
                "flex min-w-0 items-center gap-1 ps-1",
                "max-xl:w-full max-xl:flex-wrap max-xl:justify-start",
                "xl:flex-1 xl:justify-end xl:overflow-x-auto xl:overflow-y-hidden xl:overscroll-x-contain xl:py-0.5 [scrollbar-width:thin]"
              )}
            >
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 gap-1 px-2.5 text-b4 text-[var(--color-black-8)] hover:bg-black/[0.06]"
                onClick={() => setCreateOpen(true)}
              >
                <FolderPlus className="size-3.5 shrink-0" />
                <span className="hidden sm:inline">New folder</span>
              </Button>

              {extraHeaderActions}
            </div>
          </div>

          {/* Row 2: path — horizontal scroll */}
          <div className="box-border flex h-9 min-h-9 max-h-9 shrink-0 items-stretch overflow-hidden border-t border-black/[0.04] bg-[#f4f6f8] px-2 sm:px-3">
            <nav
              className="flex h-full min-h-0 w-full min-w-0 items-center overflow-x-auto overflow-y-hidden [scrollbar-width:thin]"
              aria-label="Current location"
            >
              <div className="inline-flex h-7 shrink-0 items-center gap-0.5 whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => onNavigate(null)}
                  className="flex h-7 shrink-0 items-center rounded-md px-2 text-[13px] leading-none text-[#1a5fb4] hover:bg-black/[0.05]"
                >
                  All files
                </button>
                {crumbs.map((c) => (
                  <span
                    key={c.id}
                    className="inline-flex h-7 shrink-0 items-center gap-0.5"
                  >
                    <ChevronRight className="size-3 shrink-0 text-[var(--color-black-4)]" />
                    <button
                      type="button"
                      onClick={() => onNavigate(c.id)}
                      className={cn(
                        "flex h-7 max-w-[min(12rem,45vw)] items-center truncate rounded-md px-2 text-left text-[13px] leading-none sm:max-w-[16rem]",
                        currentFolderId === c.id
                          ? "font-semibold text-[#1e302a]"
                          : "text-[#1a5fb4] hover:bg-black/[0.05]"
                      )}
                    >
                      {c.name}
                    </button>
                  </span>
                ))}
              </div>
            </nav>
          </div>
        </header>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3 sm:px-4">
          <div className="flex flex-col gap-5">{children}</div>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-[#5c6560]">
            Creates a folder inside{" "}
            <span className="font-medium text-[#1e302a]">
              {currentFolderId == null
                ? "All files"
                : crumbs[crumbs.length - 1]?.name ?? "current"}
            </span>
            .
          </p>
          <Input
            placeholder="Folder name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitCreate();
            }}
            autoFocus
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitCreate}
              disabled={!newName.trim() || createFolder.isPending}
              className="bg-[#1e302a] hover:bg-[#15231f]"
            >
              {createFolder.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={renameOpen}
        onOpenChange={(open) => {
          setRenameOpen(open);
          if (!open) {
            setRenameFolderId(null);
            setRenameName("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Folder name"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitRename();
            }}
            autoFocus
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setRenameOpen(false)}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitRename}
              disabled={
                !renameName.trim() ||
                renameFolderId == null ||
                renameFolder.isPending
              }
              className="bg-[#1e302a] hover:bg-[#15231f]"
            >
              {renameFolder.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
